import React, { useState, useEffect } from 'react';


import {
  Container,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Select,
  Checkbox,
  Button,
  Center,
  Flex,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import '../../styles/CreateUserPage.css';
import { showMessage } from '../../showMessage';

type BoatOption = { value: string; label: string };

interface GetAllBoatsResponse {
  success: boolean;
  boats: Array<{ id: number; name: string; loa: number }>;
  message?: string;
}

interface RegisterUserResponse {
  success: boolean;
  message?: string;
}

type FormValues = {
  name: string;
  lastName: string;
  email: string;
  password: string;
  boatName: string;
  boatType: string; // Select returns string; convert to number for API
  isDefaultBoat: boolean;
};

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL ?? '');
const COLOR_PRIMARY = 'var(--primary-admin)';
const COLOR_ACCENT = 'var(--accent-admin)';
const COLOR_CONTRAST = 'var(--contrast-admin)';
const REQUIRED_MARK_COLOR = 'var(--contrast-admin)';
const NOTIF_ERROR = 'notifications.error' as const;

export const CreateUserPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [boatOptions, setBoatOptions] = useState<BoatOption[]>([]);

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      lastName: '',
      email: '',
      password: '',
      boatName: '',
      boatType: '',
      isDefaultBoat: false,
    },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : t('admin.invalidEmail')),
      password: (value) => (value.length < 8 ? t('admin.passwordMinLength') : null),
      boatType: (value) => (value ? null : t('admin.selectBoatType')),
    },
  });

  useEffect(() => {
    const fetchBoats = async (): Promise<void> => {
      const tokenRaw = localStorage.getItem('token');
      if (!tokenRaw) {
        showMessage('error', t(NOTIF_ERROR), t('admin.missingAuthToken'));
        return;
      }
      const token: string = tokenRaw;

      try {
        const response = await axios.post<GetAllBoatsResponse>(
          `${API_BASE_URL}/api/user/getAllBoats`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data.success) {
          const options: BoatOption[] = response.data.boats.map((b) => ({
            value: String(b.id),
            label: `${b.name} (${b.loa}ft)`,
          }));
          setBoatOptions(options);
        } else {
          showMessage('error', t(NOTIF_ERROR), response.data.message ?? t('admin.failedLoadBoats'));
        }
      } catch {
        showMessage('error', t(NOTIF_ERROR), t('admin.networkLoadBoats'));
      }
    };

    void fetchBoats();
  }, []);

  const handleSubmit = async (values: FormValues): Promise<void> => {
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      showMessage('error', t(NOTIF_ERROR), t('admin.missingAuthToken'));
      return;
    }
    const token: string = tokenRaw;

    try {
      const payload = {
        ...values,
        boatType: Number.parseInt(values.boatType, 10),
      };

      const response = await axios.post<RegisterUserResponse>(
        `${API_BASE_URL}/api/user/registerUserBridge`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        showMessage('success', t('notifications.success'), t('admin.userCreated'));
        form.reset();
      } else {
        showMessage('error', t(NOTIF_ERROR), response.data.message ?? t('admin.userCreationFailed'));
      }
    } catch {
      showMessage('error', t(NOTIF_ERROR), t('admin.networkCreateUser'));
    }
  };

  const inputStyles = {
    root: 'createuser-input-root',
    input: 'createuser-input',
    label: 'createuser-input-label',
  } as const;

  return (
    <Container
      fluid
      style={{
        backgroundColor: COLOR_PRIMARY,
        padding: '2rem',
        borderRadius: '8px',
      }}
    >
      <Center>
        <Title order={2} style={{ color: COLOR_CONTRAST }}>{t('admin.registerNewUser')}</Title>
      </Center>

      <Paper
        shadow="md"
        radius="md"
        p="xl"
        style={{ backgroundColor: COLOR_ACCENT, marginTop: '2rem' }}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Flex direction="column" gap="md">
            <TextInput
              label={t('admin.firstName')}
              placeholder={t('admin.enterFirstName')}
              required
              classNames={inputStyles}
              styles={{ required: { color: REQUIRED_MARK_COLOR } }}
              {...form.getInputProps('name')}
            />

            <TextInput
              label={t('admin.lastName')}
              placeholder={t('admin.enterLastName')}
              required
              classNames={inputStyles}
              styles={{ required: { color: REQUIRED_MARK_COLOR } }}
              {...form.getInputProps('lastName')}
            />

            <TextInput
              label={t('auth.email')}
              placeholder={t('admin.userEmailPlaceholder')}
              required
              classNames={inputStyles}
              styles={{ required: { color: REQUIRED_MARK_COLOR } }}
              {...form.getInputProps('email')}
            />

            <PasswordInput
              label={t('auth.password')}
              placeholder={t('admin.enterPassword')}
              required
              classNames={inputStyles}
              styles={{ required: { color: REQUIRED_MARK_COLOR } }}
              {...form.getInputProps('password')}
            />

            <TextInput
              label={t('admin.boatName')}
              placeholder={t('admin.boatNamePlaceholder')}
              required
              classNames={inputStyles}
              styles={{ required: { color: REQUIRED_MARK_COLOR } }}
              {...form.getInputProps('boatName')}
            />

            <Select
              label={t('admin.boatType')}
              placeholder={t('admin.boatTypePlaceholder')}
              searchable
              clearable
              required
              data={boatOptions}
              classNames={inputStyles}
              styles={{ required: { color: REQUIRED_MARK_COLOR } }}
              {...form.getInputProps('boatType')}
            />

            <Checkbox
              label={t('admin.setDefaultBoat')}
              color={COLOR_CONTRAST}
              styles={{
                icon: { color: COLOR_PRIMARY },
                label: { color: COLOR_CONTRAST },
              }}
              {...form.getInputProps('isDefaultBoat', { type: 'checkbox' })}
            />

            <Button type="submit" className="createuser-button">{t('admin.createUserButton')}</Button>
          </Flex>
        </form>
      </Paper>
    </Container>
  );
};
